/**
 * Templates Repository
 *
 * Manages report template storage and retrieval in IndexedDB.
 * Templates define structure only, no data values.
 */

import { db, type TemplateRow } from '../db';
import type { ReportTemplate } from '../schemas';
import { DEFAULT_TEMPLATE_GROUPS } from '../templates';
import { validateTemplate } from '../template-validation';
import { AbstractBaseRepo } from './baseRepo';

const shouldValidateTemplates =
  typeof import.meta !== 'undefined' ? (import.meta.env?.DEV ?? false) : false;

class TemplatesRepoImpl extends AbstractBaseRepo<
  ReportTemplate,
  ReportTemplate,
  TemplateRow,
  ReportTemplate,
  string
> {
  protected readonly table = db.templates;

  // Templates are bundled with the app and read-only - no sync needed
  protected readonly useSyncMetadata = false;

  private normalizeTemplate(template: ReportTemplate): ReportTemplate {
    const groupLookup = new Map(DEFAULT_TEMPLATE_GROUPS.map((group) => [group.id, group]));

    const normalizedSections = template.sections.map((section) => {
      if (section.id !== 'sec_verifica-infrastrutture-di-sicurezza') {
        return section;
      }

      const targetGroup = groupLookup.get('verifiche-sopralluogo');
      if (!targetGroup || section.navigationGroup?.id === targetGroup.id) {
        return section;
      }

      return {
        ...section,
        navigationGroup: { ...targetGroup },
      };
    });

    const hasChanges = normalizedSections.some(
      (section, index) => section !== template.sections[index]
    );

    return hasChanges
      ? {
          ...template,
          sections: normalizedSections,
        }
      : template;
  }

  protected getRowId(row: TemplateRow): string {
    return row._key;
  }

  protected getInputId(input: ReportTemplate): string | null {
    return `${input.id}@${input.version}`;
  }

  protected toListModel(row: TemplateRow): ReportTemplate {
    return row as unknown as ReportTemplate;
  }

  protected toGetModel(row: TemplateRow): ReportTemplate {
    return row as unknown as ReportTemplate;
  }

  protected toRow(
    template: ReportTemplate,
    ctx: { now: string; existing?: TemplateRow }
  ): TemplateRow {
    const key = `${template.id}@${template.version}`;
    return {
      ...template,
      _key: key,
      // Sync metadata - will be overwritten by baseRepo.save()
      syncUpdatedAt: ctx.existing?.syncUpdatedAt ?? ctx.now,
      syncDeletedAt: ctx.existing?.syncDeletedAt,
      syncDeviceId: ctx.existing?.syncDeviceId,
      syncVersion: ctx.existing?.syncVersion ?? 0,
    } satisfies TemplateRow;
  }

  /**
   * Get a specific template version
   */
  async getVersion(templateId: string, version: string): Promise<ReportTemplate | null> {
    try {
      const key = `${templateId}@${version}`;
      const template = await this.get(key);
      return template ? this.normalizeTemplate(template) : null;
    } catch (error) {
      console.error(`Error getting template ${templateId}@${version}:`, error);
      return null;
    }
  }

  /**
   * Get the latest version of a template
   */
  async getLatest(templateId: string): Promise<ReportTemplate | null> {
    try {
      const templates = await db.templates
        .where('id')
        .equals(templateId)
        .sortBy('metadata.updatedAt');

      if (templates.length === 0) {
        return null;
      }

      return this.normalizeTemplate(templates[templates.length - 1] as unknown as ReportTemplate);
    } catch (error) {
      console.error(`Error getting latest template ${templateId}:`, error);
      return null;
    }
  }

  /**
   * Get the default template (latest version of tpl-ammonia-audit)
   */
  async getDefault(): Promise<ReportTemplate | null> {
    return this.getLatest('tpl-ammonia-audit');
  }

  /**
   * Save or update a template
   */
  async upsert(template: ReportTemplate): Promise<void> {
    try {
      if (shouldValidateTemplates) {
        const validation = validateTemplate(template);
        if (!validation.valid) {
          console.error(
            `Template validation failed for ${template.id}@${template.version}`,
            validation.errors
          );
          throw new Error(
            `Invalid template ${template.id}@${template.version}; fix validation errors before saving.`
          );
        }
        if (validation.warnings.length > 0) {
          console.warn(
            `Template validation warnings for ${template.id}@${template.version}`,
            validation.warnings
          );
        }
      }

      await this.save(template);
    } catch (error) {
      console.error('Error upserting template:', error);
      throw error;
    }
  }

  /**
   * List all versions of a template
   */
  async listVersions(templateId: string): Promise<ReportTemplate[]> {
    try {
      const templates = await db.templates
        .where('id')
        .equals(templateId)
        .sortBy('metadata.createdAt');
      return templates as unknown as ReportTemplate[];
    } catch (error) {
      console.error(`Error listing versions for ${templateId}:`, error);
      return [];
    }
  }

  /**
   * List all templates
   */
  async listAll(): Promise<ReportTemplate[]> {
    try {
      return await this.list();
    } catch (error) {
      console.error('Error listing all templates:', error);
      return [];
    }
  }

  /**
   * Delete a specific template version
   */
  override async delete(id: string): Promise<void>;
  async delete(templateId: string, version: string): Promise<void>;
  async delete(templateIdOrKey: string, version?: string): Promise<void> {
    try {
      const key = version ? `${templateIdOrKey}@${version}` : templateIdOrKey;
      await super.delete(key);
    } catch (error) {
      console.error(
        version
          ? `Error deleting template ${templateIdOrKey}@${version}:`
          : `Error deleting template ${templateIdOrKey}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete all versions of a template
   */
  async deleteAll(templateId: string): Promise<void> {
    try {
      await db.templates.where('id').equals(templateId).delete();
    } catch (error) {
      console.error(`Error deleting all versions of ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Check if templates table is initialized
   */
  async isInitialized(): Promise<boolean> {
    try {
      const count = await db.templates.count();
      return count > 0;
    } catch (error) {
      console.error('Error checking template initialization:', error);
      return false;
    }
  }

  /**
   * Initialize templates table with default template
   */
  async initialize(defaultTemplate: ReportTemplate): Promise<void> {
    try {
      const latest = await this.getVersion(defaultTemplate.id, defaultTemplate.version);

      if (!latest) {
        await this.upsert(defaultTemplate);
        console.log(`Initialized templates with ${defaultTemplate.id}@${defaultTemplate.version}`);
      } else {
        console.log(`Template ${defaultTemplate.id}@${defaultTemplate.version} already present`);
      }
    } catch (error) {
      console.error('Error initializing templates:', error);
      throw error;
    }
  }
}

export const TemplatesRepo = new TemplatesRepoImpl();
